import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  postLoginBusinessesSelector,
  allBusinessLenderStatusesSelector,
  pageTitleSubmit,
  businessList,
  useIsMobile
} from '../../recoil';
import PropTypes from 'prop-types';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';
import {pageNames} from '../../helpers';
import { extractPathInfo } from '../../helpers/extractPathInfo';
import {
  SelectedBusiness,
  // SelectedNewProduct,
  // NewProductSvg,
  Business,
  LogoutSvg,
  UpArrow,
  DownArrow
} from '../../assets';
import { StaticRoutes } from '../../Router';
import { TootipComp } from '../Tooltip/TooltipComp';
import './sidebar.css';
import { useTealium } from '../../tealium/useTealium';

const BusinessName = ({ name, isMobile }) => {
  if (isMobile) {
    return <span className='text-break'>{name}</span>;
  }
  return <TootipComp text={name} />;
};

BusinessName.propTypes = {
  name: PropTypes.string,
  isMobile: PropTypes.bool
};

const ProductName = ({ product, isMobile }) => {
  const displayName = product?.productCode === 'RCF' ? 'Cash Flow Facility' : product?.product;
  if (isMobile) {
    return <span className='ml-10 text-truncate'><span className='text-break'>{displayName}</span></span>;
  }
  return <TootipComp text={displayName} />;
};

ProductName.propTypes = {
  product: PropTypes.shape({
    productCode: PropTypes.string,
    product: PropTypes.string
  }),
  isMobile: PropTypes.bool
};

const getProductsForBusiness = (allBusinessLenderStatuses, businessId) => {
  const businessEntry = allBusinessLenderStatuses?.[businessId];
  if (!businessEntry) {
    return [];
  }
  return Object.keys(businessEntry)
    .filter(key => key.includes('#'))
    .map(key => ({
      applicationId: businessEntry[key].applicationReference ?? businessEntry[key].applicationId,
      product: businessEntry[key].productDisplayName ?? businessEntry[key].productName,
      productCode: businessEntry[key].productCode,
      status: businessEntry[key].status,
      productKey: key
    }));
};

export const Sidebar = ({ setSideNavOpen = () => { } }) => {
  const isMobile = useRecoilValue(useIsMobile);

  const businesses = useRecoilValue(postLoginBusinessesSelector);
  const allBusinessLenderStatuses = useRecoilValue(allBusinessLenderStatusesSelector);
  const fallbackBusinessList = useRecoilValue(businessList);
  const [ , setPageTitle] = useRecoilState(pageTitleSubmit);
  const navigate = useNavigate();
  const location = useLocation();
  const { track, events } = useTealium();

  const effectiveBusinesses = (isMobile || !businesses?.length)
    ? fallbackBusinessList?.businesses
    : businesses;

  const [listBusiness, setBusiness] = useState(effectiveBusinesses);
  const [listProduct, setProduct] = useState([]);
  // const [isNewProductOpen, setIsNewProductOpen] = useState(location?.state !== 'submit application');
  const [isMyBusinessesOpen, setMyBusinessesOpen] = useState(false);

  const navigateTo = (path) =>
    navigate(path, {
      state: {
        returnPath: location.pathname
      }
    });

  const closeMenu = () => {
    if (isMobile) {
      setSideNavOpen(false);
    }
  };

  // --- Initialization ---

  const initializeSideNav = () => {
    let { resourceType, businessId, applicationId } = extractPathInfo(location?.pathname);

    if (applicationId && !businessId) {
      for (const [bizId, entry] of Object.entries(allBusinessLenderStatuses || {})) {
        const productKeys = Object.keys(entry).filter(k => k.includes('#'));
        const match = productKeys.find(key => entry[key].applicationId === applicationId);
        if (match) {
          businessId = bizId;
          break;
        }
      }
    }

    if (!businessId) {
      return;
    }

    const quoteAvailableStatus = allBusinessLenderStatuses?.[businessId]?.hasQuoteAvailable ?? false;

    const mappedBusinessList = effectiveBusinesses?.map(businessItem => ({
      ...businessItem,
      isSelected: businessItem?.businessId === businessId,
      isQuoteAvailable: quoteAvailableStatus,
      isMyApplicationSelected: resourceType === 'applications' && businessItem?.businessId === businessId,
      isFundingQuoteSelected: resourceType === 'quotes' && businessItem?.businessId === businessId
    }));

    setMyBusinessesOpen(true);
    setBusiness(mappedBusinessList);

    const productList = getProductsForBusiness(allBusinessLenderStatuses, businessId);
    const mappedProductList = productList.map(product => {
      if (product?.applicationId === applicationId) {
        const productName = pageNames[product?.product];
        setPageTitle({ productName });
      }
      return { ...product, isSelected: product?.applicationId === applicationId };
    });
    setProduct(mappedProductList);

    if (applicationId || resourceType === 'quotes' || resourceType === 'applications') {
      // setIsNewProductOpen(false);
    }
  };

  useEffect(() => {
    const segments = location?.pathname?.split('/').filter(Boolean);
    const [resourceType] = segments;
    if (resourceType === 'applications') {
      initializeSideNav();
    }
  }, [location.pathname]);

  useEffect(() => {
    track(events.menubar.pageView());
  }, []);

  useEffect(() => {
    initializeSideNav();
  }, []);

  // --- Event handlers ---

  const handleClickBusines = () => {
    setMyBusinessesOpen(!isMyBusinessesOpen);
    // setIsNewProductOpen(false);
    track(events.menubar.menubarClick('my business clicked'));
  };

  const handleSelectBusiness = (selected, businessId) => {
    const quoteAvailableStatus = allBusinessLenderStatuses?.[businessId]?.hasQuoteAvailable ?? false;
    const updatedBusinessList = listBusiness?.map(businessItem => {
      if (businessItem?.businessId === businessId) {
        return { ...businessItem, isSelected: !selected, isMyApplicationSelected: false, isFundingQuoteSelected: false, isProductCatalogueSelected: false, isQuoteAvailable: quoteAvailableStatus };
      }
      return { ...businessItem, isSelected: false, isMyApplicationSelected: false, isFundingQuoteSelected: false, isProductCatalogueSelected: false, isQuoteAvailable: quoteAvailableStatus };
    });
    setBusiness(updatedBusinessList);
    // setIsNewProductOpen(false);
    setProduct(getProductsForBusiness(allBusinessLenderStatuses, businessId));
    track(events.menubar.menubarClick('business clicked'));
  };

  const handleSelectApplication = (businessId) => {
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: true, isFundingQuoteSelected: false, isProductCatalogueSelected: false })));
    setProduct(listProduct?.map(p => ({ ...p, isSelected: false })));
    // setIsNewProductOpen(false);
    track(events.menubar.menubarClick('my application clicked'));
    navigateTo(`/applications/${businessId}`);
    closeMenu();
  };

  const handleSelectFundingQuote = (businessId) => {
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: false, isFundingQuoteSelected: true, isProductCatalogueSelected: false, isQuoteAvailable: true })));
    setProduct(listProduct?.map(p => ({ ...p, isSelected: false })));
    // setIsNewProductOpen(false);
    track(events.menubar.menubarClick('funcding quote clicked'));
    navigateTo(`/quotes/${businessId}`);
    closeMenu();
  };

  const handleSelectProductCatalogue = (businessId) => {
    setBusiness(listBusiness?.map(b => ({
      ...b,
      isMyApplicationSelected: false,
      isFundingQuoteSelected: false,
      isProductCatalogueSelected: b?.businessId === businessId
    })));
    setProduct(listProduct?.map(product => ({ ...product, isSelected: false })));
    // setIsNewProductOpen(false);
    track(events.menubar.menubarClick('explore new products clicked'));
    navigateTo(`/products/${businessId}`);
    closeMenu();
  };

  // const handleClickNewProduct = () => {
  //   // setIsNewProductOpen(true);
  //   setMyBusinessesOpen(false);
  //   track(events.menubar.menubarClick('apply a for new product clicked'));
  //   navigateTo('/dashboard');
  //   closeMenu();
  // };

  const handleSelectProduct = (selectedProduct, selected) => {
    const product = listProduct.map(p => {
      if (p?.applicationId === selectedProduct.applicationId) {
        return { ...p, isSelected: !selected };
      }
      return { ...p, isSelected: false };
    });
    const productName = pageNames[selectedProduct?.product];
    setPageTitle({pageTitle: productName});
    setBusiness(listBusiness?.map(b => ({ ...b, isMyApplicationSelected: false, isFundingQuoteSelected: false })));
    // setIsNewProductOpen(false);
    setProduct(product);
    track(events.menubar.menubarClick('product clicked'));
    navigateTo(`/accounts/${selectedProduct.applicationId}`);
    closeMenu();
  };

  const containerClass = isMobile ? 'mobile-sidebar' : 'sidebar';
  const ContainerTag = isMobile ? 'div' : 'aside';
  const businessDividerClass = isMobile ? 'divider ml-20 wd-106' : 'divider ml-20 wd-109';
  const productDividerClass = isMobile ? 'divider ml-40 wd-114' : 'divider ml-40 wd-119';

  return (
    <ContainerTag className={containerClass}>
      <ul className="menu">
        {/* <li>
          <button className={isNewProductOpen ? 'selected-top-menu-item pl-0' : 'top-menu-item'} onClick={() => handleClickNewProduct()}>
            {isNewProductOpen ? <span className='left-border' >|</span> : ''}
            {isNewProductOpen ? <img src={SelectedNewProduct} alt='' /> : <img src={NewProductSvg} alt='' />} <span className='ml-10'>Apply for a new product </span>
          </button>
          <div className='divider' />
        </li> */}

        <li>
          <button
            className={isMyBusinessesOpen ? 'selected-menu pl-0' : 'menu-item'}
            onClick={() => handleClickBusines()}
          >
            {
              isMyBusinessesOpen ?
                <span className='left-border' >|</span> : ''}
            {isMyBusinessesOpen ? <img src={SelectedBusiness} alt='' /> : <img src={Business} alt='' />}  <span className='ml-10'>My Businesses</span>
            {isMyBusinessesOpen ?
              <img className='icon' src={UpArrow} alt='' /> :
              <img className='icon' src={DownArrow} alt='' />
            }
          </button>
          <div className='divider' />
          {isMyBusinessesOpen && (
            <ul className="submenu">
              {listBusiness?.map((business, index) => {
                return (<li key={`busines${index}`}>
                  <button
                    className={business?.isSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                    onClick={() => handleSelectBusiness(business?.isSelected, business?.businessId)}
                  >
                    <BusinessName name={business?.businessName} isMobile={isMobile} />
                    {business?.isSelected ?
                      <img className='icon' src={UpArrow} alt='' /> :
                      <img className='icon' src={DownArrow} alt='' />
                    }
                  </button>
                  <div className={businessDividerClass} />
                  {business?.isSelected && (
                    <ul className="submenu">
                      {business?.isQuoteAvailable &&
                      <>
                        <button className={business?.isFundingQuoteSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                          onClick={() => handleSelectFundingQuote(business?.businessId)}
                        ><span className='application-list-item-style'>.</span > <span className='ml-10'> Funding Quote</span></button>
                        <div className={productDividerClass} />
                      </>
                      }
                      <button className={business?.isMyApplicationSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                        onClick={() => handleSelectApplication(business?.businessId)}
                      ><span className='application-list-item-style'>.</span > <span className='ml-10'> My Applications</span></button>
                      <div className={productDividerClass} />
                      {
                        listProduct?.map((product, index) => {
                          return (
                            <li key={`product${index}`}>
                              { product?.product && (
                                <>
                                  <button className={product.isSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                                    onClick={() => handleSelectProduct(product, product?.isSelected)}
                                  ><span className='application-list-item-style'>.</span>
                                    <ProductName product={product} isMobile={isMobile} />
                                  </button>
                                  <div className={productDividerClass} />
                                </>
                              )
                              }
                            </li >
                          );
                        })
                      }
                      <button
                        className={business?.isProductCatalogueSelected ? 'selected-menu pl-0 pb-14 pt-12' : 'menu-item pl-0'}
                        onClick={() => handleSelectProductCatalogue(business?.businessId)}
                      >
                        <span className='application-list-item-style'>.</span>
                        <span className='ml-10'> Explore new products</span>
                      </button>
                      <div className={productDividerClass} />
                    </ul >
                  )}
                </li >);
              })}
            </ul >
          )}
        </li >
        <li>
          <button className="bottom-menu-item" onClick={() => {track(events.menubar.menubarClick('logout clicked'));navigateTo(StaticRoutes.logout);closeMenu();}} ><img src={LogoutSvg} alt='' /><span className='ml-10'>Logout</span></button><div className='divider' />
        </li>
      </ul >
    </ContainerTag >
  );
};

export const MobileSidebar = Sidebar;

Sidebar.propTypes = {
  setSideNavOpen: PropTypes.func
};
